import React, { useState, useEffect } from 'react';
import Layout from '../shared/Layout';

// ► Importa tus páginas reales
import KardexEstAdmision from './KardexEstAdmision';
import KardexEstMatriculacion from './KardexEstMatriculacion';
import KardexHistAdmision from './KardexHistAdmision';
import KardexTalleres from './KardexTalleres';
import KardexMatriculacionAprobados from './KardexMatriculacionAprobados';
import KardexCambioCarrera from './KardexCambioCarrera';
import KardexConvalidaciones from './KardexConvalidaciones';
import KardexInscripciones from './KardexInscripciones';
import KardexCursosInscripciones from './KardexCursosInscripciones';
import RegistroCursosExtensionPage from '../auth/RegistroCursosExtensionPage';
import RegistroExitosoPage from '../auth/RegistroExitosoPage';
import KardexSuspension from './KardexSuspension';
import KardexCalificaciones from './KardexCalificaciones';
import KardexProceso from './KardexProceso';
import KardexFinalPeriodo from './KardexFinalPeriodo';
import KardexActaCalificaciones from './KardexActaCalificaciones';
import KardexLicenciatura from './KardexLicenciatura';


const DashboardKardex = ({ onLogout, userName = 'Usuario', menuItems: externalMenuItems, sourceUrl, setCurrentPage }) => {

  const [menuItems, setMenuItems] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [currentPage, setCurrentPageState] = useState(null);
  const [pageData, setPageData] = useState(null);

  // Wrapper para manejar navegación interna
  const handleSetCurrentPage = (page, data = null) => {
    if (page === 'dashboard-kardex') {
      setCurrentPageState(null);
      setPageData(null);
    } else {
      setCurrentPageState(page);
      setPageData(data);
    }
  };

  // ► Normalizar menús y crear estructura jerárquica
  useEffect(() => {
    if (Array.isArray(externalMenuItems) && externalMenuItems.length > 0) {
      // Filtrar kardex_root
      const filtered = externalMenuItems.filter(m =>
        !m.label?.toLowerCase().includes('kardex') &&
        m.id !== 'docente_asistencia_preadmision' &&
        m.id !== 'docente_asistencia_matriculacion'
      );

      // Construir jerarquía desde id_padre
      const itemMap = {};
      const roots = [];

      // Primero crear todos los items
      filtered.forEach(item => {
        itemMap[item.id_menu || item.id] = {
          id: item.id || item.clave,
          label: item.label || item.etiqueta,
          id_padre: item.id_padre,
          id_menu: item.id_menu,
          orden: item.orden || 0,
          children: []
        };
      });

      // Asignar hijos a padres
      Object.values(itemMap).forEach(item => {
        if (item.id_padre && itemMap[item.id_padre]) {
          itemMap[item.id_padre].children.push(item);
        } else if (!item.id_padre || !itemMap[item.id_padre]) {
          roots.push(item);
        }
      });

      // Ordenar
      const sortByOrden = arr => {
        arr.sort((a, b) => (a.orden || 0) - (b.orden || 0));
        arr.forEach(item => item.children && sortByOrden(item.children));
      };
      sortByOrden(roots);

      // Agrupar Preadmisión y Matriculación bajo "Estudiantes"
      const preadmision = roots.find(m => m.id === 'kardex_estudiantes');
      const matriculacion = roots.find(m => m.id === 'kardex_asignaturas');

      const finalMenu = [];

      // Crear menú Estudiantes si existen esos items
      if (preadmision || matriculacion) {
        const estudiantesChildren = [];
        if (preadmision) estudiantesChildren.push({ ...preadmision, label: 'Admisión' });
        if (matriculacion) estudiantesChildren.push({ ...matriculacion, label: 'Licenciatura' });
        finalMenu.push({
          id: 'kardex_estudiantes_parent',
          label: 'Estudiantes',
          children: estudiantesChildren
        });
      }

      // Agregar resto de items (excepto los ya agrupados)
      roots.forEach(item => {
        if (item.id !== 'kardex_estudiantes' && item.id !== 'kardex_asignaturas') {
          finalMenu.push(item);
        }
      });

      // Agregar Cambio de Carrera y Convalidaciones si no existen
      if (!finalMenu.find(m => m.id === 'kardex_cambio_carrera')) {
        finalMenu.push({ id: 'kardex_cambio_carrera', label: 'Movimientos académicos', children: [] });
      }
      if (!finalMenu.find(m => m.id === 'kardex_convalidaciones')) {
        finalMenu.push({ id: 'kardex_convalidaciones', label: 'Convalidaciones', children: [] });
      }
      if (!finalMenu.find(m => m.id === 'kardex_inscripciones')) {
        finalMenu.push({ id: 'kardex_inscripciones', label: 'Registro Asignaturas', children: [] });
      }
      if (!finalMenu.find(m => m.id === 'kardex_suspension')) {
        finalMenu.push({ id: 'kardex_suspension', label: 'Suspension de Estudios', children: [] });
      }
      if (!finalMenu.find(m => m.id === 'kardex_calificaciones_parent')) {
        finalMenu.push({
          id: 'kardex_calificaciones_parent',
          label: 'Calificaciones',
          children: [
            { id: 'kardex_calificaciones_proceso', label: 'Proceso' },
            { id: 'kardex_calificaciones_final', label: 'Final Periodo' },
            { id: 'kardex_calificaciones_acta', label: 'Acta de Calificaciones' }
          ]
        });
      }
      if (!finalMenu.find(m => m.id === 'kardex_registro_asignaturas')) {
        finalMenu.push({
          id: 'kardex_registro_asignaturas',
          label: 'Homologación Asignaturas',
          children: [
            { id: 'kardex_asignaturas_licenciatura', label: 'Licenciatura' }
          ]
        });
      }

      setMenuItems(finalMenu);

      // Primer menú clickeable
      const findFirstClickable = items => {
        for (const item of items) {
          if (!item.children || item.children.length === 0) return item.id;
          const child = findFirstClickable(item.children);
          if (child) return child;
        }
        return null;
      };
      setActiveMenu(findFirstClickable(finalMenu) || finalMenu[0]?.id);
    }
  }, [externalMenuItems]);

  // ► Render de contenido según menú seleccionado
  const renderContent = () => {
    switch (activeMenu) {
      // Admisión (Preadmisión)
      case 'kardex_estudiantes':
        return <KardexEstAdmision setCurrentPage={setCurrentPage} />;

      // Matriculación
      case 'kardex_asignaturas':
        return <KardexEstMatriculacion />;

      // Historial Académico
      case 'kardex_registros':
        return <KardexHistAdmision />;

      // Cursos de Extensión > Inscripción
      case 'kardex_cursos_inscripcion':
        return <KardexCursosInscripciones setCurrentPage={handleSetCurrentPage} />;

      // Cursos de Extensión > Inscritos
      case 'kardex_est_matriculacion':
        return <KardexTalleres tab="inscritos" setCurrentPage={handleSetCurrentPage} />;

      // Aprobados para matriculación
      case 'kardex_aprobados':
        return <KardexMatriculacionAprobados />;

      // Cambio de Carrera
      case 'kardex_cambio_carrera':
        return <KardexCambioCarrera />;

      // Convalidaciones
      case 'kardex_convalidaciones':
        return <KardexConvalidaciones />;

      // Inscripciones de Asignaturas
      case 'kardex_inscripciones':
        return <KardexInscripciones />;

      case 'kardex_suspension':
        return <KardexSuspension />;

      case 'kardex_calificaciones_proceso':
        return <KardexProceso />;
      case 'kardex_calificaciones_final':
        return <KardexFinalPeriodo />;
      case 'kardex_calificaciones_acta':
        return <KardexActaCalificaciones />;
      case 'kardex_asignaturas_licenciatura':
        return <KardexLicenciatura />;

      default:
        return (
          <div className="p-8">
            <div className="bg-white rounded-lg shadow-sm p-6 text-gray-600">
              Sección no encontrada o no implementada. (Menu ID: {activeMenu})
            </div>
          </div>
        );
    }
  };

  // Si estamos en la página de registro, mostrarla sin el menú
  if (currentPage === 'registro-cursos-extension' && pageData) {
    return (
      <RegistroCursosExtensionPage
        setCurrentPage={handleSetCurrentPage}
        cursoId={pageData.id}
        cursoNombre={pageData.nombre}
        cursoCuotas={pageData.cuotas}
        cursoCosto={pageData.costo}
        returnTo="dashboard-kardex"
      />
    );
  }

  // Si estamos en la página de éxito, mostrarla
  if (currentPage === 'registro-exitoso' && pageData) {
    return (
      <RegistroExitosoPage
        setCurrentPage={handleSetCurrentPage}
        username={pageData.username}
        tipoRegistro={pageData.tipoRegistro || pageData.tipo}
        returnTo="dashboard-kardex"
      />
    );
  }

  return (
    <Layout
      userRole="Kardex"
      userName={userName}
      onLogout={onLogout}
      menuItems={menuItems}
      activeMenu={activeMenu}
      setActiveMenu={setActiveMenu}
    >
      {renderContent()}
    </Layout>
  );
};

export default DashboardKardex;

